import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, searchArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { buildBreadcrumbSchema } from "@/lib/seo";
import { formatNumber } from "@/lib/content";
import { SITE_URL } from "@/mocks/site";

type SortKey = "latest" | "hot";

export default function Today() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const activeCat = params.get("cat") ?? "all";
  const sort = (params.get("sort") as SortKey) ?? "latest";

  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const results = useMemo(() => {
    let list = searchArticles(q);
    if (activeCat !== "all") {
      list = list.filter((item) => item.categorySlug === activeCat);
    }
    return [...list].sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return a.date < b.date ? 1 : -1;
    });
  }, [q, activeCat, sort]);

  const ranking = [...articles].sort((a, b) => b.views - a.views).slice(0, 5);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam("q", keyword.trim());
  };

  const jsonLd = [
    buildBreadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "今日看点", path: "/today" },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "今日看点",
      url: `${SITE_URL}/today`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: results.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/article/${item.id}`,
          name: item.title,
        })),
      },
    },
  ];

  return (
    <>
      <Seo
        title="今日看点 - 黑料看点每日更新必看推荐"
        description="黑料看点今日看点栏目每日更新，汇总全网值得关注的热点事件与高能内容，支持按综艺、影视、网络热点等分类筛选，一页看完当日精华。"
        keywords="黑料看点今日看点,黑料看点每日更新,今日必看推荐,每日精选热点"
        path="/today"
        jsonLd={jsonLd}
      />

      <section className="bg-background-50 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 pt-6 pb-8 md:pt-8 md:pb-10">
          <Breadcrumbs items={[{ name: "今日看点" }]} />
          <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">今日看点</h1>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                每天早晚两次更新，按时间汇总当日值得关注的热点与高能内容。可以用分类筛选，也可以直接搜索关键词。
              </p>
              <p className="mt-3 text-xs text-foreground-500 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 anim-pulse-dot"></span>
                当日已收录 {articles.length} 条看点 · 最近更新 20:00
              </p>
            </div>

            <form onSubmit={handleSearch} className="flex items-center h-11 rounded-full border border-background-300 bg-background-100 pl-4 pr-1 w-full lg:w-80">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题、标签或关键词"
                aria-label="搜索看点"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 px-2 outline-none min-w-0"
              />
              <button
                type="submit"
                className="h-8 px-4 rounded-full bg-foreground-950 text-background-50 text-xs whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200 flex-wrap">
            <button
              type="button"
              onClick={() => updateParam("cat", "all")}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                activeCat === "all" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.slug}
                type="button"
                onClick={() => updateParam("cat", item.slug)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  activeCat === item.slug ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {item.shortName}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200">
            {([
              { key: "latest", label: "最新发布", icon: "ri-time-line" },
              { key: "hot", label: "最热阅读", icon: "ri-fire-line" },
            ] as { key: SortKey; label: string; icon: string }[]).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => updateParam("sort", item.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sort === item.key ? "bg-foreground-950 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                <i className={item.icon}></i>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-600">
                  共 <span className="text-foreground-950 font-medium">{results.length}</span> 条看点
                  {q && <> · 关键词「{q}」</>}
                </p>
                {(q || activeCat !== "all") && (
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>重置筛选
                  </button>
                )}
              </div>

              {results.length > 0 ? (
                <div className="flex flex-col gap-8 md:gap-9">
                  {results.map((item) => (
                    <div key={item.id} className="pb-8 md:pb-9 border-b border-background-200 last:border-b-0 last:pb-0">
                      <ArticleCard article={item} variant="wide" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-20 rounded-lg bg-background-100 border border-background-200">
                  <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
                  <p className="mt-4 text-base text-foreground-900">没有找到相关看点</p>
                  <p className="mt-1.5 text-sm text-foreground-500">试试换个关键词，或浏览其他分类。</p>
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    查看全部看点
                  </button>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950 pb-3 border-b border-background-200">
                  <i className="ri-bar-chart-box-line text-primary-600"></i>
                  阅读排行
                </h2>
                <div className="mt-1">
                  {ranking.map((item, index) => (
                    <ArticleCard key={item.id} article={item} variant="minimal" rank={index + 1} />
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950 pb-4 border-b border-background-200">
                  <i className="ri-apps-2-line text-secondary-600"></i>
                  按分类浏览
                </h2>
                <ul className="mt-3 space-y-1">
                  {categories.map((item) => {
                    const count = articles.filter((a) => a.categorySlug === item.slug).length;
                    return (
                      <li key={item.slug}>
                        <Link
                          to={`/topic/${item.slug}`}
                          className="flex items-center justify-between py-2 text-sm text-foreground-700 hover:text-primary-700 transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <i className={`${item.icon} text-foreground-500`}></i>
                            {item.name}
                          </span>
                          <span className="text-xs text-foreground-500">{count}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="rounded-lg bg-accent-100 border border-accent-200 p-5 md:p-6">
                <h2 className="text-base font-semibold text-accent-900">订阅看点日报</h2>
                <p className="mt-2 text-sm leading-relaxed text-accent-900/85">
                  不想每天自己筛，就让精选清单早上主动送到你邮箱。
                </p>
                <a
                  href="#newsletter"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent-900 hover:text-accent-800 transition-colors"
                >
                  前往订阅 <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}